import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Play, Youtube } from "lucide-react";
import { extractYoutubeId, youtubeThumb } from "@/lib/productGallery";

export const DEFAULT_SECTION_TUTORIALS = {
  dashboard: "https://youtu.be/2Tcgujq6Uwk",
  domain: "",
  email: "",
  products: "",
  custom_print: "",
  car_parts: "",
};

export const SECTION_TITLES = {
  dashboard: "Watch Tutorial On How to use your dashboard?",
  domain: "Watch Tutorial On How to Connect Your Domain?",
  email: "Watch Tutorial On How to connect your email?",
  products: "Watch Tutorial On How to add product and product with variation?",
  custom_print: "How to use custom print addon?",
  car_parts: "How to use car parts addon?",
};

export function youtubePopupEmbed(url) {
  const id = extractYoutubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?rel=0&autoplay=1` : "";
}

export function useSectionTutorials() {
  const [urls, setUrls] = useState(DEFAULT_SECTION_TUTORIALS);
  useEffect(() => {
    api.get("/i18n/languages").then(({ data }) => {
      const s = data?.sections || {};
      setUrls({
        dashboard: (s.dashboard?.youtube_url || "").trim() || DEFAULT_SECTION_TUTORIALS.dashboard,
        domain: (s.domain?.youtube_url || "").trim(),
        email: (s.email?.youtube_url || "").trim(),
        products: (s.products?.youtube_url || "").trim(),
        custom_print: (s.custom_print?.youtube_url || "").trim(),
        car_parts: (s.car_parts?.youtube_url || "").trim(),
      });
    }).catch(() => {});
  }, []);
  return urls;
}

export function YoutubePopup({ open, onOpenChange, url, title }) {
  const embed = open ? youtubePopupEmbed(url) : "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(100vw-1.25rem,52rem)] max-w-none gap-2 border-0 bg-slate-950 p-2 sm:p-3 text-white rounded-xl sm:rounded-2xl top-[50%] max-h-[calc(100dvh-1.25rem)] overflow-y-auto [&>button]:text-white [&>button]:opacity-90"
        data-testid="youtube-tutorial-popup"
      >
        <DialogHeader className="pr-8 text-left space-y-1">
          <DialogTitle className="text-sm sm:text-base font-semibold text-white leading-snug">{title}</DialogTitle>
          <DialogDescription className="sr-only">YouTube tutorial</DialogDescription>
        </DialogHeader>
        {embed ? (
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
            <iframe
              title={title}
              src={embed}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="youtube-tutorial-iframe"
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function TutorialBanner({ section, url, title, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const label = title || SECTION_TITLES[section] || "Watch tutorial";
  const id = extractYoutubeId(url);
  if (!id) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId || `tutorial-banner-${section}`}
        className={`group w-full flex items-center gap-3 rounded-2xl border border-red-200/80 bg-gradient-to-r from-red-50 via-white to-orange-50 px-3.5 py-2.5 sm:px-4 sm:py-3 text-left shadow-[0_6px_20px_-12px_rgba(220,38,38,0.45)] hover:border-red-300 hover:shadow-[0_10px_28px_-12px_rgba(220,38,38,0.55)] transition ${className}`}
      >
        <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition">
          <Youtube className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-red-600">YouTube tutorial</span>
          <span className="block text-sm sm:text-[15px] font-semibold text-slate-900 leading-snug">{label}</span>
        </span>
        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 shrink-0">
          <Play className="w-3.5 h-3.5 fill-current" /> Watch
        </span>
      </button>
      <YoutubePopup open={open} onOpenChange={setOpen} url={url} title={label} />
    </>
  );
}

export function DashboardTutorialCard({ url }) {
  const [open, setOpen] = useState(false);
  const resolved = (url || "").trim() || DEFAULT_SECTION_TUTORIALS.dashboard;
  const id = extractYoutubeId(resolved);
  const title = SECTION_TITLES.dashboard;
  if (!id) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="dash-intro-video"
        className="w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white text-left shadow-[0_8px_28px_-12px_rgba(15,23,42,0.18)] hover:shadow-[0_14px_36px_-12px_rgba(15,23,42,0.22)] transition"
      >
        <div className="flex flex-col sm:flex-row sm:items-stretch">
          <div className="relative w-full sm:w-56 md:w-72 shrink-0 aspect-video sm:aspect-auto sm:min-h-[9.5rem] bg-slate-900">
            <img src={youtubeThumb(id)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <span className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-slate-950/20" />
            <span
              className="absolute inset-0 flex items-center justify-center"
              data-testid="dash-intro-play"
            >
              <span className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg ring-4 ring-white/30">
                <Play className="w-7 h-7 fill-current ml-0.5" />
              </span>
            </span>
          </div>
          <div className="flex-1 p-4 sm:p-5 flex flex-col justify-center gap-2">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-red-600">
              <Youtube className="w-4 h-4" /> YouTube tutorial
            </div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug">{title}</h2>
            <p className="text-sm text-slate-500">A short walkthrough of your merchant dashboard. Opens in a popup so it stays easy to watch on phones.</p>
            <span className="mt-1 inline-flex items-center justify-center sm:justify-start gap-2 self-stretch sm:self-start rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2">
              <Play className="w-4 h-4 fill-current" /> Watch now
            </span>
          </div>
        </div>
      </button>
      <YoutubePopup open={open} onOpenChange={setOpen} url={resolved} title={title} />
    </>
  );
}
